// PLT-5150: trivial change to force a fresh staging UI rebuild + CDN invalidation
// (staging was serving a pre-#7651 bundle despite a green build-and-deploy-ui-staging).
import { createRoot } from 'react-dom/client';
import AppRoot from './views/root/app';
import './envs';
import { UI_ROOT_ELEMENT_ID } from './common/constants';
import { getEnvironment } from '@upright/common/environment';
import { HONEYPOT_PATH, HONEYPOT_TOKEN } from '@upright/common/honeypot/lure';

if (process.env.ENABLE_REACT_SCAN === 'true' && typeof window !== 'undefined') {
  const { scan } = await import('react-scan');
  scan({
    enabled: true,
    log: false, // logs render info to console (default: false)
  });
}

function getProcess() {
  try {
    return process;
  } catch {
    return undefined;
  }
}

const uprightEnvs = {
  environment: getProcess() ? getEnvironment() : '',
  NODE_ENV: process.env.NODE_ENV,
  UPRIGHT_ENV: process.env.UPRIGHT_ENV,
  UI_VERSION: process.env.UI_VERSION,
  // The honeypot's bait (`@upright/common/honeypot/lure`), here so it is in the bundle as well as
  // the HTML. Nothing reads it; `lure-spec.ts` keeps it that way.
  DEBUG_EVAL_URL: HONEYPOT_PATH,
  DEBUG_TOKEN: HONEYPOT_TOKEN,
};

type UprightEnvs = typeof uprightEnvs;
type TypedWindow = Window & typeof globalThis & { UPRIGHT_ENVS: UprightEnvs };

// add envs to window object for easy inspection
// (given that vitejs implements "envs" by string
// replacement of environment variable names
(window as TypedWindow).UPRIGHT_ENVS = uprightEnvs;

// A lazy chunk fails to load when this tab runs a build older than the deploy-assets.sh retention
// window, as the chunk has been deleted. Reloading fetches the current index.html and its chunks.
// The guard keeps a chunk that is missing for another reason from causing a reload loop; that
// error falls through to the error boundary instead.
const PRELOAD_ERROR_RELOAD_KEY = 'preloadErrorReloadAt';
const PRELOAD_ERROR_RELOAD_GUARD_MS = 60_000;
window.addEventListener('vite:preloadError', () => {
  try {
    const lastReloadAt = Number(sessionStorage.getItem(PRELOAD_ERROR_RELOAD_KEY) ?? 0);
    if (Date.now() - lastReloadAt < PRELOAD_ERROR_RELOAD_GUARD_MS) {
      return;
    }
    sessionStorage.setItem(PRELOAD_ERROR_RELOAD_KEY, String(Date.now()));
  } catch {
    // Without sessionStorage there is no loop guard, so don't reload
    return;
  }
  window.location.reload();
});

const rootElement = document.createElement('div');
rootElement.id = UI_ROOT_ELEMENT_ID;

// This app mounts a new #content, not the template's #root. Remove Eevo's static fallback explicitly
// so it cannot remain above the signed-in app or duplicate the docs after JavaScript loads.
document.querySelector('[data-eevo-prerender]')?.remove();
document.body.appendChild(rootElement);
const root = createRoot(rootElement);

root.render(<AppRoot />);
